'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { hasRole } from '@/lib/roles';
import type { UserRole } from '@/types/next-auth';
import { HelpCircle, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HelpMarkdown } from '@/components/help/help-markdown';
import { HelpFeedback, type HelpIssueReportContext } from '@/components/help/help-feedback';
import { ScreenHelp } from '@/components/help/screen-help';
import { getScreenHints } from '@/lib/help/screen-hints';
import { cn } from '@/lib/utils';
import { matchHelpDocForPath } from '@/lib/help/match';

interface ModalDoc { id: string; title: string; body: string; draft: boolean; screens: string[] }
/** staffOnly 갈래는 담당자에게만 목차에 선다(`/help` 목차와 같다). */
interface ModalSection { title: string; ids: string[]; staffOnly?: boolean }
interface HelpPayload { docs: ModalDoc[]; sections: ModalSection[] }

export function HelpButton({
  pathname,
  iconOnly = false,
  className,
  issueReport,
  userRole,
  firstVisitKey,
}: {
  pathname: string | null;
  /** 담당자 갈래(프로그램 운영·관리자)를 목차에 세울지. 역할을 아는 앱 틀이 넘긴다. 없으면 학생 목차다. */
  userRole?: UserRole;
  iconOnly?: boolean;
  className?: string;
  /** 작성 화면처럼 제 신고 문맥이 있는 화면이 넘긴다. 도움말 안의 「문제 신고」가 그 문맥으로 간다(점검 N-S17). */
  issueReport?: HelpIssueReportContext;
  /** 이 브라우저에서 처음 들어왔을 때 화면 안내를 한 번 저절로 연다(사용자 10-06). 열 때가 되면 값을 넘기고, 그 전에는 비운다. */
  firstVisitKey?: string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [payload, setPayload] = useState<HelpPayload | null>(null);
  // 담당자 갈래(프로그램 운영·관리자)는 담당자에게만 목차에 세운다. 학생 화면에 「결재」를 두지 않는다(10-05 6회차 H1-0).
  const staff = hasRole(userRole, 'STAFF');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [screenPath, setScreenPath] = useState<string | null>(null);
  const previousPath = useRef(pathname);
  const scrollRef = useRef<HTMLDivElement>(null);
  const screenHints = getScreenHints(pathname);
  const screenOpen = screenPath === pathname && screenHints !== null;
  const helpOpen = screenOpen || open;

  useEffect(() => {
    if (previousPath.current !== pathname) {
      previousPath.current = pathname;
      setScreenPath(null);
    }
  }, [pathname]);

  const hasScreenHints = screenHints !== null;
  useEffect(() => {
    if (!firstVisitKey || !hasScreenHints) return;
    let seen = false;
    try {
      seen = window.localStorage.getItem(firstVisitKey) === '1';
      if (!seen) window.localStorage.setItem(firstVisitKey, '1');
    } catch { /* 저장소를 못 쓰면 저절로 열지 않는다(열 때마다 뜨지 않게) */ seen = true; }
    if (!seen) setScreenPath(pathname);
  }, [firstVisitKey, hasScreenHints, pathname]);

  const changeDoc = (id: string) => {
    setActiveId(id);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  const openHelp = async () => {
    if (screenHints) {
      setScreenPath(screenOpen ? null : pathname);
      return;
    }
    setOpen(true);
    setLoading(true);
    setFailed(false);
    try {
      const response = await fetch('/help/data');
      if (!response.ok) throw new Error('도움말을 읽지 못했습니다');
      const next: HelpPayload = await response.json();
      setPayload(next);
      const matched = matchHelpDocForPath(next.docs, pathname ?? '');
      setActiveId(matched && !matched.draft ? matched.id : null);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const current = payload?.docs.find((doc) => doc.id === activeId);
  const section = payload?.sections.find((item) => item.ids.includes(activeId ?? ''));
  const docMap = new Map(payload?.docs.map((doc) => [doc.id, doc]) ?? []);
  const resolvedBody = current?.body.replace(/\[\[([a-z0-9-]+)\]\]/g, (_, id: string) => {
    const target = docMap.get(id);
    return target ? (target.draft ? target.title : `[${target.title}](/help/${id})`) : id;
  });

  return (
    <>
      <button
        type="button"
        aria-label={iconOnly ? '도움말' : undefined}
        aria-pressed={screenHints ? screenOpen : undefined}
        title={iconOnly ? '도움말' : undefined}
        onClick={openHelp}
        className={cn(
          'flex items-center gap-3 rounded-md border-l-[3px] text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          iconOnly ? 'size-9 justify-center pr-[3px]' : 'w-full py-2.5 pl-[13px] pr-4 text-left',
          helpOpen
            ? 'border-l-primary bg-primary-subtle text-primary-subtle-foreground hover:bg-primary-subtle'
            : 'border-l-transparent text-foreground hover:bg-muted/70',
          className,
        )}
      >
        <HelpCircle className={cn('size-4 shrink-0', !helpOpen && 'text-muted-foreground')} aria-hidden="true" />
        {!iconOnly && <span>도움말</span>}
      </button>
      {screenOpen && screenHints && <ScreenHelp set={screenHints} onClose={() => setScreenPath(null)} />}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="flex max-h-[85dvh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-5 pr-12 text-left">
            <DialogTitle className="text-lg">{current?.title ?? '도움말'}</DialogTitle>
            <Link href={current ? `/help/${current.id}` : '/help'} target="_blank" rel="noreferrer" className="inline-flex w-fit items-center gap-1 text-sm text-primary hover:underline">
              새 탭에서 열기 <ExternalLink className="size-3.5" aria-hidden="true" />
            </Link>
          </DialogHeader>
          <div ref={scrollRef} className="min-h-0 overflow-y-auto px-6 py-5">
            {loading ? (
              <p className="text-sm text-muted-foreground">도움말을 불러오는 중입니다.</p>
            ) : failed ? (
              <p className="text-sm text-muted-foreground">도움말을 열지 못했습니다. 새 탭에서 다시 열어주세요.</p>
            ) : current ? (
              <>
                <HelpMarkdown markdown={resolvedBody ?? ''} onInternalLink={changeDoc} />
                {section && (
                  <nav aria-label={`${section.title} 문서`} className="mt-10 border-t pt-5">
                    <h3 className="mb-3 text-sm font-semibold">{section.title}</h3>
                    <ul className="space-y-1">
                      {section.ids.filter((id) => docMap.has(id)).map((id) => (
                        <li key={id}>{docMap.get(id)!.draft ? <span className="px-2 py-1.5 text-sm text-muted-foreground">{docMap.get(id)!.title} · 준비 중</span> : <button type="button" aria-current={activeId === id ? 'page' : undefined} className="rounded-md px-2 py-1.5 text-sm text-primary hover:bg-primary-subtle" onClick={() => changeDoc(id)}>{docMap.get(id)!.title}</button>}</li>
                      ))}
                    </ul>
                  </nav>
                )}
                <div className="mt-8"><HelpFeedback docId={current.id} issueReport={issueReport} /></div>
              </>
            ) : (
              <div className="space-y-7">
                {payload?.sections.filter((item) => staff || !item.staffOnly).map((item) => (
                  <section key={item.title}>
                    <h3 className="mb-2 text-sm font-semibold">{item.title}</h3>
                    <ul className="space-y-1">
                      {item.ids.filter((id) => docMap.has(id)).map((id) => (
                        <li key={id}>{docMap.get(id)!.draft ? <span className="px-2 py-1.5 text-sm text-muted-foreground">{docMap.get(id)!.title} · 준비 중</span> : <button type="button" className="rounded-md px-2 py-1.5 text-sm text-primary hover:bg-primary-subtle" onClick={() => changeDoc(id)}>{docMap.get(id)!.title}</button>}</li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
